import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {CatalogBrowserService} from "../../services/catalog-browser.service";
import {NotificationService} from "../../services/notification.service";
import {Router} from "@angular/router";
import {TransferProcessStates} from "../../models/transfer-process-states";
import {ContractOffer} from "../../models/contract-offer";
import {NegotiationResult} from "../../models/negotiation-result";
//IDOIA: edc-connector-client version 0.4.0
//import {ContractNegotiation, ContractNegotiationRequest} from "../../../mgmt-api-client/model";
import {ContractNegotiation, ContractNegotiationRequest, PolicyBuilder} from "../../../mgmt-api-client/model";

interface RunningTransferProcess {
  processId: string;
  assetId?: string;
  state: TransferProcessStates;
}

@Component({
  selector: 'edc-demo-catalog-browser',
  templateUrl: './catalog-browser.component.html',
  styleUrls: ['./catalog-browser.component.scss']
})
export class CatalogBrowserComponent implements OnInit, OnDestroy {

  filteredContractOffers: ContractOffer[] = [];
  allContractOffers: ContractOffer[] = [];
  searchText = '';
  selectedConnector = ''; // Will be set dynamically to first connector
  availableConnectors: { key: string, name: string, address: string }[] = [];
  runningTransferProcesses: RunningTransferProcess[] = [];
  runningNegotiations: Map<string, NegotiationResult> = new Map<string, NegotiationResult>(); // contractOfferId, NegotiationResult
  finishedNegotiations: Map<string, ContractNegotiation> = new Map<string, ContractNegotiation>(); // contractOfferId, contractAgreementId
  isLoading = true;
  private pollingHandleNegotiation?: any;

  constructor(private apiService: CatalogBrowserService,
              public dialog: MatDialog,
              private router: Router,
              private notificationService: NotificationService,
              @Inject('HOME_CONNECTOR_STORAGE_ACCOUNT') private homeConnectorStorageAccount: string) {
  }

  ngOnInit(): void {
    // Load available connectors and set first as default
    this.availableConnectors = this.apiService.getAvailableConnectors();
    if (this.availableConnectors.length > 0) {
      this.selectedConnector = this.availableConnectors[0].key;
      this.fetchContractOffers(this.selectedConnector);
    } else {
      this.isLoading = false;
    }
  }

  ngOnDestroy(): void {
    if (this.pollingHandleNegotiation) {
      clearInterval(this.pollingHandleNegotiation);
    }
  }

  onSearch() {
    this.applySearchFilter();
  }

  getDcatType(contractOffer: any): string | undefined {
    if (contractOffer && contractOffer['dcat:dataset'] && contractOffer.assetId) {
      const dataset = contractOffer['dcat:dataset'].find((ds: any) => ds['@id'] === contractOffer.assetId);
      return dataset?.['@type'];
    }
    return undefined;
  }

  getDisplayedPolicy(contractOffer: any): any | undefined {
    if (contractOffer?.policy) {
      return contractOffer.policy;
    }

    const dataset = contractOffer?.['dcat:dataset']?.find((ds: any) => ds['@id'] === contractOffer.assetId);
    return dataset?.['odrl:hasPolicy'];
  }

  getPolicyId(policy: any): string | undefined {
    return policy?.['@id'] || policy?.uid;
  }

  getPolicyPermissions(policy: any): any[] {
    const permission = policy?.['odrl:permission'] ?? policy?.permission;
    if (!permission) {
      return [];
    }
    return Array.isArray(permission) ? permission : [permission];
  }

  getPolicyProhibitions(policy: any): any[] {
    const prohibition = policy?.['odrl:prohibition'] ?? policy?.prohibition;
    if (!prohibition) {
      return [];
    }
    return Array.isArray(prohibition) ? prohibition : [prohibition];
  }

  getPolicyObligations(policy: any): any[] {
    const obligation = policy?.['odrl:obligation'] ?? policy?.obligation;
    if (!obligation) {
      return [];
    }
    return Array.isArray(obligation) ? obligation : [obligation];
  }

  getPolicyConstraints(permission: any): any[] {
    const constraint = permission?.['odrl:constraint'] ?? permission?.constraint;
    if (!constraint) {
      return [];
    }
    return Array.isArray(constraint) ? constraint : [constraint];
  }

  getPermissionAction(permission: any): string {
    const action = permission?.['odrl:action'] ?? permission?.action;
    if (typeof action === 'string') {
      return action;
    }
    return action?.['@id'] || '-';
  }

  getConstraintValue(constraint: any, key: string): string {
    const value = constraint?.[key];
    if (typeof value === 'string') {
      return value;
    }
    return value?.['@id'] || '-';
  }

  getFormattedRightOperand(constraint: any): string {
    const rightOperand = constraint?.['odrl:rightOperand'] ?? constraint?.rightOperand;
    if (typeof rightOperand === 'object' && rightOperand !== null) {
      const rawValue = rightOperand['@value'];
      if (rawValue !== undefined && rawValue !== null) {
        return this.formatAsDateTimeIfIso(rawValue);
      }
    }
    return this.formatAsDateTimeIfIso(rightOperand);
  }

  private formatAsDateTimeIfIso(value: unknown): string {
    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value !== 'string') {
      return String(value);
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return '-';
    }

    const isoUtcDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
    if (!isoUtcDateRegex.test(trimmedValue)) {
      return trimmedValue;
    }

    const parsedDate = new Date(trimmedValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return trimmedValue;
    }

    const day = String(parsedDate.getUTCDate()).padStart(2, '0');
    const month = String(parsedDate.getUTCMonth() + 1).padStart(2, '0');
    const year = parsedDate.getUTCFullYear();
    const hours = String(parsedDate.getUTCHours()).padStart(2, '0');
    const minutes = String(parsedDate.getUTCMinutes()).padStart(2, '0');
    const seconds = String(parsedDate.getUTCSeconds()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  }

  onConnectorChange() {
    console.log('Connector changed to:', this.selectedConnector);
    this.searchText = '';
    this.fetchContractOffers(this.selectedConnector);
  }

  selectReadonlyField(event: FocusEvent): void {
    const input = event.target as HTMLInputElement | null;
    input?.select();
  }

  copyToClipboard(value: string): void {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(value).catch(() => {
        // Ignore clipboard failures to avoid interrupting user flow.
      });
    }
  }

  private fetchContractOffers(connectorKey: string): void {
    this.isLoading = true;
    this.apiService.getContractOffers(connectorKey).subscribe({
      next: offers => {
        this.allContractOffers = offers;
        this.applySearchFilter();
        this.isLoading = false;
      },
      error: error => {
        console.error('Error fetching catalog:', error);
        this.allContractOffers = [];
        this.filteredContractOffers = [];
        this.isLoading = false;
      },
      complete: () => {
        console.log('Fetched contract offers:', this.allContractOffers);
      }
    });
  }

  private applySearchFilter(): void {
    const normalizedSearch = this.searchText.trim().toLowerCase();

    this.filteredContractOffers = normalizedSearch
      ? this.allContractOffers.filter(contractOffer => {
          const offerId = contractOffer.id?.toLowerCase() ?? '';
          const assetId = contractOffer.assetId?.toLowerCase() ?? '';
          const name = contractOffer.properties?.name?.toLowerCase() ?? '';
          const type = contractOffer.properties?.type?.toLowerCase() ?? '';
          return (
            assetId.includes(normalizedSearch) ||
            name.includes(normalizedSearch) ||
            type.includes(normalizedSearch) ||
            offerId.includes(normalizedSearch)
          );
        })
      : this.allContractOffers;

  }

  onNegotiateClicked(contractOffer: ContractOffer) {
    const initiateRequest: ContractNegotiationRequest = {
      //IDOIA: edc-connector-client version 0.4.0
      //connectorAddress: contractOffer.originator,
      //offer: {
      //  offerId: contractOffer.id,
      //  assetId: contractOffer.assetId,
      //  policy: contractOffer.policy,
      //},
      //connectorId: 'connector',
      //providerId: contractOffer["dcat:service"].id
      counterPartyAddress: contractOffer.originator,
      //IDOIA: edc-connector-client version 0.4.0
      //policy: contractOffer.policy
      policy: new PolicyBuilder()
          .raw({
            ...contractOffer.policy,
            target: contractOffer.assetId,
            assigner: contractOffer.assigner
          })
          .type("Offer")
          .build(),
    };


    const finishedNegotiationStates = [
      "VERIFIED",
      "TERMINATED",
      "ERROR"];

    this.apiService.initiateNegotiation(initiateRequest).subscribe(negotiationId => {
      //IDOIA: edc-connector-client version 0.4.0
      //this.finishedNegotiations.delete(initiateRequest.offer.offerId);
      this.finishedNegotiations.delete(negotiationId);
      //IDOIA: edc-connector-client version 0.4.0
      //this.runningNegotiations.set(initiateRequest.offer.offerId, {
      this.runningNegotiations.set(negotiationId, {
        id: negotiationId,
        //IDOIA: edc-connector-client version 0.4.0
        //offerId: initiateRequest.offer.offerId
        offerId: negotiationId
      });

      // Show success notification
      this.notificationService.showInfo("Contract negotiation started successfully!", "View Contracts", () => {
        this.router.navigate(['/contracts'])
      });

      if (!this.pollingHandleNegotiation) {
        // there are no active negotiations
        this.pollingHandleNegotiation = setInterval(() => {
          // const finishedNegotiations: NegotiationResult[] = [];

          for (const negotiation of this.runningNegotiations.values()) {
            this.apiService.getNegotiationState(negotiation.id).subscribe(updatedNegotiation => {
              if (finishedNegotiationStates.includes(updatedNegotiation.state!)) {
                let offerId = negotiation.offerId;
                this.runningNegotiations.delete(offerId);
                if (updatedNegotiation["edc:state"] === "VERIFIED") {
                  this.finishedNegotiations.set(offerId, updatedNegotiation);
                  this.notificationService.showInfo("Contract Negotiation complete!", "Show me!", () => {
                    this.router.navigate(['/contracts'])
                  })
                }
              }

              if (this.runningNegotiations.size === 0) {
                clearInterval(this.pollingHandleNegotiation);
                this.pollingHandleNegotiation = undefined;
              }
            });
          }
        }, 1000);
      }
    }, error => {
      console.error(error);
      this.notificationService.showError("Error starting negotiation");
    });
  }

  isBusy(contractOffer: ContractOffer) {
    return this.runningNegotiations.get(contractOffer.id) !== undefined || !!this.runningTransferProcesses.find(tp => tp.assetId === contractOffer.assetId);
  }

  getState(contractOffer: ContractOffer): string {
    const transferProcess = this.runningTransferProcesses.find(tp => tp.assetId === contractOffer.assetId);
    if (transferProcess) {
      return TransferProcessStates[transferProcess.state];
    }

    const negotiation = this.runningNegotiations.get(contractOffer.id);
    if (negotiation) {
      return 'negotiating';
    }

    return '';
  }

  isNegotiated(contractOffer: ContractOffer) {
    return this.finishedNegotiations.get(contractOffer.id) !== undefined;
  }

}
